// =============================================================================
// SevricLoader — the single loading screen for every SEVRIC surface.
//
// Variant B from the picker: hex mark with pulsing cobalt halo on the left,
// hairline divider, "SEVRIC" wordmark + product name (Caveat handwritten)
// stacked on the right.
//
// Usage:
//   <SevricLoader app="recon" />
//   <SevricLoader app="battery care" size="inline" />
//   <SevricLoader app="chat" size="small" />
//
// Props:
//   app  — product name shown in Caveat (e.g. "recon", "battery care", "chat",
//          "portal"). If omitted, just shows the SEVRIC wordmark.
//   size — "full" (default, fills viewport), "inline" (panel-sized), "small"
//          (widget-sized).
//
// No "Minting", no "Tuning the wire", no text-spinner shame. One mark, one
// motion, one wordmark, everywhere.
// =============================================================================
import React from "react";

function MarkSvg({ size }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 240 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{
        display: "block",
        position: "relative",
        animation: "sevric-loader-pulse 1.8s ease-in-out infinite",
        filter: "drop-shadow(0 2px 6px rgba(46,86,148,0.20))",
      }}
    >
      <polygon
        points="200,120 160,189.28 80,189.28 40,120 80,50.72 160,50.72"
        fill="none"
        stroke="#2E5694"
        strokeWidth="14"
        strokeLinejoin="miter"
      />
      <polygon
        points="156,120 138,151.18 102,151.18 84,120 102,88.82 138,88.82"
        fill="#2E5694"
      />
    </svg>
  );
}

const DIMS = {
  full:   { mark: 96, divH: 72, wordSize: 22, wordSpace: 0.38, appSize: 46 },
  inline: { mark: 64, divH: 48, wordSize: 15, wordSpace: 0.34, appSize: 28 },
  small:  { mark: 36, divH: 28, wordSize: 11, wordSpace: 0.30, appSize: 18 },
};

export function SevricLoader({ app, size = "full" }) {
  const d = DIMS[size] || DIMS.full;
  const isFull = size === "full";
  const isSmall = size === "small";
  // Small/inline-widget loaders skip the wordmark — just the pulsing mark.
  const showLockup = !isSmall;
  const stageStyle = isFull
    ? {
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg, #F6F6F3)",
      }
    : {
        padding: isSmall ? "16px 12px" : "40px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      };

  return (
    <div style={stageStyle} role="status" aria-label={`Loading SEVRIC ${app || ""}`.trim()}>
      <style>{`
        @keyframes sevric-loader-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.06); }
        }
        @keyframes sevric-loader-halo {
          0%   { transform: scale(0.85); opacity: 0.55; }
          100% { transform: scale(1.7); opacity: 0; }
        }
      `}</style>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: isFull ? 32 : (size === "small" ? 14 : 22),
        }}
      >
        {/* MARK + HALO */}
        <div
          style={{
            position: "relative",
            width: d.mark + 16,
            height: d.mark + 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              position: "absolute",
              inset: 8,
              borderRadius: "50%",
              background: "#2E5694",
              opacity: 0,
              animation: "sevric-loader-halo 1.8s ease-out infinite",
            }}
          />
          <span
            style={{
              position: "absolute",
              inset: 8,
              borderRadius: "50%",
              background: "#2E5694",
              opacity: 0,
              animation: "sevric-loader-halo 1.8s ease-out infinite 0.6s",
            }}
          />
          <MarkSvg size={d.mark} />
        </div>

        {showLockup && (
        <>
        {/* DIVIDER */}
        <div
          style={{
            width: 1,
            height: d.divH,
            background: "rgba(20,18,12,0.07)",
            flexShrink: 0,
          }}
        />

        {/* LOCKUP */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 8,
            minWidth: 0,
          }}
        >
          <div
            style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontWeight: 700,
              fontSize: d.wordSize,
              letterSpacing: `${d.wordSpace}em`,
              color: "var(--text, #14161A)",
              textTransform: "uppercase",
              lineHeight: 1,
            }}
          >
            SEVRIC
          </div>
          {app && (
            <div
              style={{
                fontFamily: "'Caveat', cursive",
                fontWeight: 700,
                fontSize: d.appSize,
                color: "var(--accent, #2E5694)",
                letterSpacing: "-0.005em",
                transform: "rotate(-1.5deg)",
                transformOrigin: "0 50%",
                lineHeight: 1,
              }}
            >
              {app}
            </div>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}

export default SevricLoader;
